// Angular imports
import { Injectable, inject } from '@angular/core';

// Project libraries
import { PlatformInfoService } from '@tgx/shared/services';

// Local imports
import { BillingContractCreate } from '../interface/billing-contract';
import { BillingContractPartnerType } from '../enum/billing-partner-type';
import { BillingSubTypes, Partner } from '../interface/billing-account';
import { CreateBillingContractParamData } from '../interface/onboarding';
import { DocusignContractData } from '../interface/docusign';
import { DocusignContractType as ContractType } from '../enum/docusign-contract-types';
import { OnboardingBillingService } from './onboarding-billing.service';

@Injectable({ providedIn: 'root' })
export class OnboardingAgreementsService {
  private billingService = inject(OnboardingBillingService);
  private platformInfoService = inject(PlatformInfoService);

  private readonly referenceTCP = 'TCP Framework';
  private readonly referenceAnnex = 'TCP Annex';
  private readonly contractRateTypeGMV = 'GMV' as const;
  private readonly contractPartnerModelTypeCM23 = 'CM23' as const;
  private isBuyer = false;
  private isSeller = false;

  createSellerFakeContract = false;
  hasAgreementAnnexBuyer = false;
  hasAgreementAnnexSeller = false;
  hasAgreementTCP = false;

  constructor() {
    this.platformInfoService.organizationSelected$.subscribe({
      next: (org) => {
        if (org && org.crmID) {
          this.isBuyer = org.isBuyer;
          this.isSeller = org.isSeller;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  //check if has the respective required agreements to enable submit button
  checkAgreements(agreements: DocusignContractData[]): boolean {
    // agreements that are required to enable the submit button
    if (agreements?.length === 0) {
      return false;
    }
    let hasAgreements: boolean;

    const requiredContracts = [{ type: ContractType.TCP, key: 'hasAgreementTCP' }];

    if (this.isBuyer) {
      requiredContracts.push({ type: ContractType.ANNEX_BUYER, key: 'hasAgreementAnnexBuyer' });
    }
    if (this.isSeller) {
      requiredContracts.push({ type: ContractType.ANNEX_SELLER, key: 'hasAgreementAnnexSeller' });
    }

    // verify status of the agreements
    requiredContracts.forEach((contract) => {
      const agreement = agreements.find((agreement) => agreement.contractType === contract.type);
      this[contract.key] = agreement?.status === 'Completed';
    });

    const hasUncompletedAgreement = agreements.some((agreement) => agreement.status !== 'Completed');

    if (this.isSeller && !this.isBuyer) {
      const hasSellerAgreement = agreements.some((agreement) => agreement.contractType === ContractType.ANNEX_SELLER);

      if (!hasSellerAgreement) {
        // not have seller agreement, can create fake seller agreement
        hasAgreements = true;
        this.createSellerFakeContract = true;
        return hasAgreements;
      }
    }

    hasAgreements =
      !hasUncompletedAgreement &&
      this.hasAgreementTCP &&
      (!this.isBuyer || this.hasAgreementAnnexBuyer) &&
      (!this.isSeller || this.hasAgreementAnnexSeller);
    return hasAgreements;
  }

  async createBillingContract(data: CreateBillingContractParamData) {
    try {
      const { agreements, billingPartner, billingSubTypes } = data;

      const promises = [];

      if (this.createSellerFakeContract) {
        const fakeSellerContractData: BillingContractCreate = this.generateBodyBillingContractData(
          ContractType.DIRECT_PRODUCT,
          null, // do not have agreement.id
          billingPartner,
          billingSubTypes,
          true, // is a fake contract
        );

        if (fakeSellerContractData) {
          promises.push(this.billingService.createContract(fakeSellerContractData));
        }
      }

      for (const agreement of agreements) {
        if (agreement.status === 'Completed') {
          const createData: BillingContractCreate = this.generateBodyBillingContractData(
            agreement.contractType,
            agreement.contractId,
            billingPartner,
            billingSubTypes,
            false, // not fake contract
            agreement.sendTime ? new Date(agreement.sendTime) : new Date(),
          );

          if (createData) {
            promises.push(this.billingService.createContract(createData));
          }
        }
      }
      await Promise.all(promises);
    } catch (error: any) {
      throw `·Error create billing contract: ${error.message || error}`;
    }
  }

  private generateBodyBillingContractData(
    contractType: string,
    agreementId: string,
    billingPartner: Partner,
    billingSubTypes: BillingSubTypes[],
    createSellerFakeContract: boolean,
    agreementSignDate: Date = new Date(),
  ): BillingContractCreate {
    const baseData: BillingContractCreate = {
      partnerType: null,
      // modelType: null,
      // ContractType: null,
      BillingAccount: null,
      reference: '',
      rateType: null,
      pdfBlobId: agreementId,
      minimumFee: '0',
      lookToBook: '0',
      minimumUnit: '0',
      currency: 'EUR',
      Partner: { connect: { id: billingPartner.id } },
      activationDateTime: agreementSignDate,
      bookCheckinNorDate: true,
      enabled: true,
    };

    // seller fake contract
    if (createSellerFakeContract) {
      const directProductSubType = billingSubTypes.find((subType) => subType.name === ContractType.DIRECT_PRODUCT);
      const sellerPartnerType = BillingContractPartnerType.SELLER;

      return {
        ...baseData,
        partnerType: sellerPartnerType,
        modelType: this.contractPartnerModelTypeCM23,
        ContractType: { connect: { id: directProductSubType.id } },
        BillingAccount: { connect: { id: billingPartner.BillingAccounts[0].id } },
        reference: `CM-${sellerPartnerType}-${this.contractPartnerModelTypeCM23}`,
        rateType: this.contractRateTypeGMV,
      };
    }

    // contract TCP, SLA, SLA_BUYER, SLA_SELLER
    if (
      contractType === ContractType.TCP ||
      contractType === ContractType.SLA_BUYER ||
      contractType === ContractType.SLA_SELLER
    ) {
      const subType = billingSubTypes.find((subType) => subType.name === contractType);
      return {
        ...baseData,
        partnerType: BillingContractPartnerType.OTHER,
        ContractType: { connect: { id: subType.id } },
        BillingAccount: { connect: { id: billingPartner.BillingAccounts[0].id } },
        reference: contractType === ContractType.TCP ? this.referenceTCP : contractType,
      };
    }

    // contract ANNEX_BUYER or ANNEX_SELLER
    if (contractType === ContractType.ANNEX_BUYER || contractType === ContractType.ANNEX_SELLER) {
      const partnerType: BillingContractPartnerType =
        contractType === ContractType.ANNEX_BUYER
          ? BillingContractPartnerType.BUYER
          : BillingContractPartnerType.SELLER;

      const billingAccountId =
        this.isBuyer && this.isSeller && billingPartner.BillingAccounts.length > 1
          ? contractType === ContractType.ANNEX_BUYER
            ? billingPartner.BillingAccounts[0]?.id
            : billingPartner.BillingAccounts[1]?.id
          : billingPartner.BillingAccounts[0]?.id;

      return {
        ...baseData,
        partnerType: partnerType,
        modelType: this.contractPartnerModelTypeCM23,
        BillingAccount: { connect: { id: billingAccountId } },
        reference: this.referenceAnnex,
        rateType: this.contractRateTypeGMV,
      };
    }

    return null;
  }
}
